import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  MessageCircle,
  Clock,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  RefreshCw,
  Users,
  AlertTriangle,
  BookOpen,
} from 'lucide-react';

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-medium text-gray-900">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-3">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function Support() {
  const { t } = useTranslation();

  const faqs = [
    { question: t('publicPages.support.faqs.q1'), answer: t('publicPages.support.faqs.a1') },
    { question: t('publicPages.support.faqs.q2'), answer: t('publicPages.support.faqs.a2') },
    { question: t('publicPages.support.faqs.q3'), answer: t('publicPages.support.faqs.a3') },
    { question: t('publicPages.support.faqs.q4'), answer: t('publicPages.support.faqs.a4') },
    { question: t('publicPages.support.faqs.q5'), answer: t('publicPages.support.faqs.a5') },
    { question: t('publicPages.support.faqs.q6'), answer: t('publicPages.support.faqs.a6') },
    { question: t('publicPages.support.faqs.q7'), answer: t('publicPages.support.faqs.a7') },
    { question: t('publicPages.support.faqs.q8'), answer: t('publicPages.support.faqs.a8') },
  ];

  const contactMethods = [
    {
      icon: Mail,
      title: t('publicPages.support.contactMethods.emailSupport'),
      description: t('publicPages.support.contactMethods.emailSupportDesc'),
      action: 'support@clareefai.com',
      href: 'mailto:support@clareefai.com',
      cta: t('publicPages.support.contactMethods.sendEmail'),
    },
    {
      icon: Clock,
      title: t('publicPages.support.contactMethods.businessHours'),
      description: t('publicPages.support.contactMethods.businessHoursDesc'),
      action: t('publicPages.support.contactMethods.businessHoursValue'),
      href: null,
      cta: null,
    },
    {
      icon: BookOpen,
      title: t('publicPages.support.contactMethods.documentation'),
      description: t('publicPages.support.contactMethods.documentationDesc'),
      action: null,
      href: '/docs',
      cta: t('publicPages.support.contactMethods.viewDocumentation'),
    },
  ];

  const topics = [
    { icon: Users, label: t('publicPages.support.topics.connecting'), anchor: '#faq' },
    { icon: Shield, label: t('publicPages.support.topics.privacy'), anchor: '#faq' },
    { icon: RefreshCw, label: t('publicPages.support.topics.disconnecting'), anchor: '#faq' },
    { icon: AlertTriangle, label: t('publicPages.support.topics.troubleshooting'), anchor: '#faq' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/">
            <img src="/logo-color.png" alt="Clareefai" className="h-8 w-auto" />
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/docs" className="text-gray-600 hover:text-gray-900 transition-colors">
              {t('publicPages.docs.nav.docs')}
            </Link>
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              {t('publicPages.docs.nav.signIn')}
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="flex items-center gap-2 text-sm text-blue-600 font-medium mb-4">
            <MessageCircle className="w-4 h-4" />
            {t('publicPages.support.eyebrow')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t('publicPages.support.hero.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {t('publicPages.support.hero.subtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Contact Methods */}
        <section className="mb-14">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('publicPages.support.getInTouch')}</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {contactMethods.map((method) => {
              const Icon = method.icon;
              return (
                <div
                  key={method.title}
                  className="border border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{method.title}</h3>
                  <p className="text-sm text-gray-500 mb-3">{method.description}</p>
                  {method.action && (
                    <p className="text-sm font-medium text-gray-700">{method.action}</p>
                  )}
                  {method.href && method.cta && (
                    method.href.startsWith('mailto:') ? (
                      <a
                        href={method.href}
                        className="inline-block mt-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        {method.cta} &rarr;
                      </a>
                    ) : (
                      <Link
                        to={method.href}
                        className="inline-block mt-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        {method.cta} &rarr;
                      </Link>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Quick Help Topics */}
        <section className="mb-14">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('publicPages.support.commonTopics')}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {topics.map((topic) => {
              const Icon = topic.icon;
              return (
                <a
                  key={topic.label}
                  href={topic.anchor}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 border border-gray-200 transition-colors"
                >
                  <Icon className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{topic.label}</span>
                </a>
              );
            })}
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="scroll-mt-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{t('publicPages.support.faqTitle')}</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FaqItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </section>

        {/* Still need help */}
        <section className="mt-14 bg-gray-50 rounded-2xl p-8 text-center">
          <h2 className="text-lg font-bold text-gray-900 mb-2">{t('publicPages.support.stillNeedHelp')}</h2>
          <p className="text-gray-600 mb-5 text-sm">
            {t('publicPages.support.stillNeedHelpDesc')}
          </p>
          <a
            href="mailto:support@clareefai.com"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Mail className="w-4 h-4" />
            {t('publicPages.support.contactSupport')}
          </a>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <p>© {new Date().getFullYear()} Clareefai. {t('publicPages.docs.footer.rights')}</p>
            <div className="flex items-center gap-6">
              <Link to="/docs" className="hover:text-white transition-colors">
                {t('publicPages.docs.footer.documentation')}
              </Link>
              <Link to="/support" className="hover:text-white transition-colors">
                {t('publicPages.docs.footer.support')}
              </Link>
              <a
                href="https://clareefai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                clareefai.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
